import { router, useForm } from '@inertiajs/react';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Sparkles, Save, X, Eye, HelpCircle } from "lucide-react";
import AppLayout from '@/layouts/AppLayout';
import { useConfirm } from '@/hooks/useConfirm';

export default function CelebrationEventForm({
  celebration_event = null,
  trigger_types = {},
  available_departments = [],
  available_locations = []
}) {
  const isEditing = !!celebration_event;
  const { confirm, ConfirmDialog } = useConfirm();
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [backgroundType, setBackgroundType] = useState('gradient'); // 'gradient', 'upload', 'url'
  const [imagePreview, setImagePreview] = useState(celebration_event?.background_image_preview_url || null);

  const form = useForm({
    name: celebration_event?.name || '',
    trigger_type: celebration_event?.trigger_type || '',
    trigger_conditions: celebration_event?.trigger_conditions || {},
    points_amount: celebration_event?.points_amount || 500,
    message_template: celebration_event?.message_template || '',
    hashtag: celebration_event?.hashtag || '',
    active: celebration_event?.active ?? true,
    repeatable: celebration_event?.repeatable ?? false,
    priority: celebration_event?.priority || 0,
    milestone_config: celebration_event?.milestone_config || { enabled: false, mode: 'every_year', milestones: [] },
    custom_date: celebration_event?.custom_date || '',
    audience_mode: celebration_event?.audience_mode || 'everyone',
    target_audience: celebration_event?.target_audience || { departments: [], locations: [] },
    visibility: celebration_event?.visibility || 'company',
    background_image_url: celebration_event?.background_image_url || ''
  });
  const { data, setData, processing, errors } = form;

  const triggerOptions = Object.entries(trigger_types).map(([value, label]) => ({
    value,
    label
  }));

  useEffect(() => {
    if (celebration_event?.background_image_preview_url) {
      setBackgroundType('upload');
    } else if (data.background_image_url && data.background_image_url.startsWith('linear-gradient')) {
      setBackgroundType('gradient');
    } else {
      setBackgroundType('gradient');
    }
  }, []);

  const handleTriggerTypeChange = (triggerType) => {
    // Actualizar tipo de trigger
    setData({
      ...data,
      trigger_type: triggerType,
      trigger_conditions: {}, // Reset conditions
      message_template: getDefaultMessage(triggerType),
      hashtag: getDefaultHashtag(triggerType)
    });
  };

  const getDefaultMessage = (triggerType) => {
    const messages = {
      'birthday': '¡Feliz cumpleaños {{user_name}}! 🎂 Te deseamos un día increíble. Disfruta de {{points}} puntos.',
      'work_anniversary': '¡Feliz aniversario {{user_name}}! 🎉 Gracias por ser parte del equipo. Aquí tienes {{points}} puntos.',
      'new_user': '¡Bienvenido al equipo {{user_name}}! 👋 Aquí tienes {{points}} puntos para empezar.',
      'custom_date': '¡Feliz celebración {{user_name}}! 📅 Disfruta de {{points}} puntos en este día especial.'
    };
    return messages[triggerType] || '';
  };

  const getDefaultHashtag = (triggerType) => {
    const hashtags = {
      'birthday': '#cumpleaños',
      'work_anniversary': '#aniversario',
      'new_user': '#bienvenida',
      'custom_date': '#celebración'
    };
    return hashtags[triggerType] || '';
  };

  const renderConditionsForm = () => {
    switch (data.trigger_type) {
      case 'new_user':
        return (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Días Después del Registro *
            </label>
            <Input
              type="number"
              min="1"
              max="30"
              value={data.trigger_conditions.days_after_creation || 1}
              onChange={(e) => setData({
                ...data,
                trigger_conditions: {
                  ...data.trigger_conditions,
                  days_after_creation: parseInt(e.target.value) || 1
                }
              })}
              placeholder="1"
            />
            <p className="text-xs text-gray-500 mt-1">
              El evento se ejecuta X días después de que el usuario se registró
            </p>
          </div>
        );

      case 'custom_date':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fecha de la Celebración *
              </label>
              <Input
                type="date"
                value={data.custom_date}
                onChange={(e) => setData('custom_date', e.target.value)}
                placeholder="Selecciona una fecha"
                required
              />
            </div>

            <div className="flex items-center justify-between bg-purple-50 p-3 rounded-lg border border-purple-200">
              <div>
                <p className="text-sm font-semibold text-gray-700">Repetir cada año</p>
                <p className="text-xs text-gray-500">El evento se ejecutará automáticamente en esta fecha cada año</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.repeatable}
                  onChange={(e) => setData('repeatable', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            <p className="text-xs text-gray-500">
              {data.repeatable
                ? 'Todos los usuarios activos recibirán puntos en esta fecha cada año'
                : 'Los usuarios recibirán puntos solo en la fecha específica seleccionada'}
            </p>
          </div>
        );

      case 'birthday':
      case 'work_anniversary':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700 flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Este tipo de evento no requiere configuración adicional
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    form.transform((d) => ({
      celebration_event: d,
      ...(isEditing && { _method: 'patch' })
    }));

    if (isEditing) {
      form.post(`/admin/celebration_events/${celebration_event.id}`, {
        forceFormData: true,
        preserveScroll: true,
        onSuccess: () => router.visit('/admin')
      });
    } else {
      form.post('/admin/celebration_events', {
        forceFormData: true,
        preserveScroll: true,
        onSuccess: () => router.visit('/admin')
      });
    }
  };

  const handlePreview = async () => {
    if (!isEditing) {
      await confirm({
        title: 'Guarda primero',
        message: 'Primero guarda el evento para ver una vista previa.',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'warning'
      });
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await fetch(`/admin/celebration_events/${celebration_event.id}/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });

      const result = await response.json();
      if (result.success) {
        setPreviewData(result.preview);
        setShowPreview(true);
      } else {
        await confirm({
          title: 'Error en vista previa',
          message: 'Error al generar vista previa: ' + result.error,
          confirmText: 'Entendido',
          cancelText: '',
          variant: 'danger'
        });
      }
    } catch (error) {
      await confirm({
        title: 'Error en vista previa',
        message: 'Error al generar vista previa.',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'danger'
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-purple-500" />
            {isEditing ? 'Editar evento de celebración' : 'Nuevo evento de celebración'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditing ? 'Modifica la configuración del evento' : 'Configura un nuevo evento para otorgar puntos automáticamente'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
                Configuración del Evento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre del Evento *
                </label>
                <Input
                  type="text"
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                  placeholder="Ej: 5 Reconocimientos Recibidos"
                  required
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>

              {/* Trigger Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Trigger *
                </label>
                <select
                  value={data.trigger_type}
                  onChange={(e) => handleTriggerTypeChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Selecciona un tipo de trigger...</option>
                  {triggerOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.trigger_type && <p className="text-xs text-red-500 mt-1">{errors.trigger_type}</p>}
              </div>

              {/* Trigger Conditions (dynamic based on trigger_type) */}
              {data.trigger_type && renderConditionsForm()}

              {/* Points Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Puntos a Otorgar *
                </label>
                <Input
                  type="number"
                  min="1"
                  max="10000"
                  step="1"
                  value={data.points_amount}
                  onChange={(e) => setData('points_amount', parseInt(e.target.value) || 0)}
                  placeholder="500"
                  className="text-lg font-semibold"
                  required
                />
                {errors.points_amount && <p className="text-xs text-red-500 mt-1">{errors.points_amount}</p>}
              </div>

              {/* Milestone Configuration for Work Anniversary */}
              {data.trigger_type === 'work_anniversary' && (
                <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-700">Configuración de Milestones</p>
                      <p className="text-xs text-gray-500">Personaliza puntos según años de aniversario</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.milestone_config.enabled}
                        onChange={(e) => setData({
                          ...data,
                          milestone_config: {
                            ...data.milestone_config,
                            enabled: e.target.checked
                          }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  {data.milestone_config.enabled && (
                    <div className="space-y-3 mt-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Modo de Milestones
                        </label>
                        <select
                          value={data.milestone_config.mode}
                          onChange={(e) => setData({
                            ...data,
                            milestone_config: {
                              ...data.milestone_config,
                              mode: e.target.value
                            }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="every_year">Mismo monto cada año</option>
                          <option value="custom_amounts">Montos personalizados por año</option>
                          <option value="specific_years">Solo años específicos</option>
                        </select>
                      </div>

                      {(data.milestone_config.mode === 'custom_amounts' || data.milestone_config.mode === 'specific_years') && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Milestones por Año
                          </label>
                          <div className="space-y-2">
                            {(data.milestone_config.milestones || []).map((milestone, index) => (
                              <div key={index} className="flex gap-2 items-center bg-white p-2 rounded border border-gray-200">
                                <Input
                                  type="number"
                                  placeholder="Año"
                                  min="1"
                                  max="50"
                                  value={milestone.year}
                                  onChange={(e) => {
                                    const newMilestones = [...data.milestone_config.milestones];
                                    newMilestones[index].year = parseInt(e.target.value) || 0;
                                    setData({
                                      ...data,
                                      milestone_config: {
                                        ...data.milestone_config,
                                        milestones: newMilestones
                                      }
                                    });
                                  }}
                                  className="w-24"
                                />
                                <Input
                                  type="number"
                                  placeholder="Puntos"
                                  min="1"
                                  max="10000"
                                  value={milestone.points}
                                  onChange={(e) => {
                                    const newMilestones = [...data.milestone_config.milestones];
                                    newMilestones[index].points = parseInt(e.target.value) || 0;
                                    setData({
                                      ...data,
                                      milestone_config: {
                                        ...data.milestone_config,
                                        milestones: newMilestones
                                      }
                                    });
                                  }}
                                  className="flex-1"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newMilestones = data.milestone_config.milestones.filter((_, i) => i !== index);
                                    setData({
                                      ...data,
                                      milestone_config: {
                                        ...data.milestone_config,
                                        milestones: newMilestones
                                      }
                                    });
                                  }}
                                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const newMilestones = [...(data.milestone_config.milestones || []), { year: 1, points: 2000 }];
                                setData({
                                  ...data,
                                  milestone_config: {
                                    ...data.milestone_config,
                                    milestones: newMilestones
                                  }
                                });
                              }}
                              className="w-full px-3 py-2 text-sm text-purple-600 hover:bg-purple-100 rounded border border-purple-200"
                            >
                              + Agregar Milestone
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {data.milestone_config.mode === 'custom_amounts'
                              ? 'Define puntos personalizados para años específicos. Los años no listados usarán el monto base.'
                              : 'Solo se ejecutará el evento en los años especificados.'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Message Template */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Mensaje de Celebración *
                  </label>
                  {isEditing && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handlePreview}
                      disabled={loadingPreview}
                      className="text-purple-600 hover:text-purple-700"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      {loadingPreview ? 'Cargando...' : 'Vista Previa'}
                    </Button>
                  )}
                </div>
                <textarea
                  value={data.message_template}
                  onChange={(e) => setData('message_template', e.target.value)}
                  placeholder="¡Felicidades {{user_name}}! Has alcanzado un logro importante."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Variables disponibles:{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{{user_name}}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{{first_name}}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{{points}}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{{years}}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{{years_ordinal}}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{{date}}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{{company_name}}'}</code>
                </p>
                {errors.message_template && <p className="text-xs text-red-500 mt-1">{errors.message_template}</p>}
              </div>

              {/* Hashtag */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Hashtag
                </label>
                <Input
                  type="text"
                  value={data.hashtag}
                  onChange={(e) => setData('hashtag', e.target.value)}
                  placeholder="#logro"
                  className="font-mono"
                />
              </div>

              <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/30">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Fondo de la Celebración
                </label>

                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Tipo de Fondo</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setBackgroundType('gradient');
                        setData('background_image_url', '');
                        setData('background_image', null);
                        setImagePreview(null);
                      }}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        backgroundType === 'gradient'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-300'
                      }`}
                    >
                      Degradado
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBackgroundType('upload');
                        setData('background_image_url', '');
                      }}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        backgroundType === 'upload'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-300'
                      }`}
                    >
                      Subir Imagen
                    </button>
                  </div>
                </div>

                {backgroundType === 'gradient' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Elige un Degradado</label>
                    <div className="grid grid-cols-5 gap-2">
                      {[
                        { name: 'Púrpura', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
                        { name: 'Rosa', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
                        { name: 'Azul', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
                        { name: 'Verde', value: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
                        { name: 'Amarillo', value: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
                        { name: 'Océano', value: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' },
                        { name: 'Pastel', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
                        { name: 'Coral', value: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' },
                        { name: 'Durazno', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
                        { name: 'Arcoíris', value: 'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)' }
                      ].map((gradient, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setData('background_image_url', gradient.value)}
                          className={`h-12 rounded-lg transition-all ${
                            data.background_image_url === gradient.value
                              ? 'ring-4 ring-purple-600 ring-offset-2'
                              : 'ring-1 ring-gray-300 hover:ring-2 hover:ring-purple-400'
                          }`}
                          style={{ background: gradient.value }}
                          title={gradient.name}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {backgroundType === 'upload' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Subir Imagen</label>
                    <div className="space-y-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setData('background_image', file);
                            // Preview
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setImagePreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                      />
                      {imagePreview && (
                        <div className="mt-2 relative">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-full h-32 object-cover rounded-lg border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setData('background_image', null);
                              setImagePreview(null);
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-2">
                  Este fondo se mostrará en el feed cuando se publique la celebración
                </p>
              </div>

              {/* Repeatable Toggle */}
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div>
                  <p className="font-semibold text-gray-700">¿Evento Repetible?</p>
                  <p className="text-xs text-gray-500">El usuario puede ganar este evento múltiples veces</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.repeatable}
                    onChange={(e) => setData('repeatable', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div>
                  <p className="font-semibold text-gray-700">Estado del Evento</p>
                  <p className="text-xs text-gray-500">¿Debe ejecutarse este evento automáticamente?</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.active}
                    onChange={(e) => setData('active', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    {data.active ? 'Activo' : 'Inactivo'}
                  </span>
                </label>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  ¿Cómo funcionan las celebraciones?
                </h4>
                <ul className="text-xs text-blue-700 space-y-1 ml-5 list-disc">
                  <li><strong>Eventos de Fecha:</strong> Se ejecutan automáticamente cada día a las 8:00 AM</li>
                  <li><strong>Eventos de Acción:</strong> Se ejecutan en tiempo real cuando ocurre la acción</li>
                  <li>Se crea un <strong>Recognition automático</strong> del sistema al usuario</li>
                  <li>Los puntos se suman al saldo del usuario automáticamente</li>
                  <li>Se envía notificación a Basecamp</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                🎯 Audiencia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Audience Mode */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ¿Quién recibirá esta celebración? *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="everyone"
                      checked={data.audience_mode === 'everyone'}
                      onChange={(e) => setData('audience_mode', e.target.value)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Todos los usuarios</p>
                      <p className="text-xs text-gray-500">Todos los usuarios activos elegibles</p>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="specific_groups"
                      checked={data.audience_mode === 'specific_groups'}
                      onChange={(e) => setData('audience_mode', e.target.value)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Grupos específicos</p>
                      <p className="text-xs text-gray-500">Filtrar por departamento o ubicación</p>
                    </div>
                  </label>
                </div>
              </div>

              {data.audience_mode === 'specific_groups' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  {/* Departments */}
                  {available_departments.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Departamentos
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {available_departments.map((dept) => (
                          <label key={dept} className="flex items-center space-x-2 p-2 bg-white border rounded hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={data.target_audience.departments?.includes(dept) || false}
                              onChange={(e) => {
                                const departments = data.target_audience.departments || [];
                                const newDepartments = e.target.checked
                                  ? [...departments, dept]
                                  : departments.filter(d => d !== dept);
                                setData('target_audience', {
                                  ...data.target_audience,
                                  departments: newDepartments
                                });
                              }}
                              className="w-4 h-4 text-purple-600"
                            />
                            <span className="text-sm text-gray-700">{dept}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {available_locations.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Ubicaciones
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {available_locations.map((loc) => (
                          <label key={loc} className="flex items-center space-x-2 p-2 bg-white border rounded hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={data.target_audience.locations?.includes(loc) || false}
                              onChange={(e) => {
                                const locations = data.target_audience.locations || [];
                                const newLocations = e.target.checked
                                  ? [...locations, loc]
                                  : locations.filter(l => l !== loc);
                                setData('target_audience', {
                                  ...data.target_audience,
                                  locations: newLocations
                                });
                              }}
                              className="w-4 h-4 text-purple-600"
                            />
                            <span className="text-sm text-gray-700">{loc}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    💡 Los usuarios que pertenezcan a <strong>cualquiera</strong> de los grupos seleccionados recibirán la celebración
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                👁️ Visibilidad
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ¿Quién puede ver esta celebración? *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="company"
                      checked={data.visibility === 'company'}
                      onChange={(e) => setData('visibility', e.target.value)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Visible para toda la empresa</p>
                      <p className="text-xs text-gray-500">Todos pueden ver el reconocimiento en el feed</p>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="private"
                      checked={data.visibility === 'private'}
                      onChange={(e) => setData('visibility', e.target.value)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Privado</p>
                      <p className="text-xs text-gray-500">Solo visible para el usuario que recibe la celebración</p>
                    </div>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <Button
              type="submit"
              disabled={processing}
              className="bg-purple-600 hover:bg-purple-700 text-white flex-1"
            >
              <Save className="w-4 h-4 mr-2" />
              {processing ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear Evento'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => router.visit('/admin')}
              className="text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
          </div>
        </form>

        {showPreview && previewData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-purple-600" />
                  Vista Previa en el Feed
                </h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 bg-gray-50">
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                  <div
                    className="relative px-6 py-12 flex flex-col items-center justify-center text-center overflow-hidden"
                    style={
                      imagePreview
                        ? {
                            backgroundImage: `url(${imagePreview})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }
                        : data.background_image_url && !data.background_image_url.startsWith('linear-gradient')
                        ? {
                            backgroundImage: `url(${data.background_image_url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }
                        : {
                            background: data.background_image_url || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          }
                    }
                  >
                    <div className="absolute inset-0 bg-black/20"></div>

                    <div className="relative z-10 mb-4">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 ring-4 ring-white shadow-2xl flex items-center justify-center text-white font-bold text-2xl">
                        {previewData.user_name?.charAt(0) || 'U'}
                      </div>
                    </div>

                    <div className="relative z-10 max-w-md">
                      <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-xl">
                        <div className="text-lg font-bold text-gray-900 mb-2">
                          {previewData.user_name}
                        </div>
                        <div
                          className="text-sm text-gray-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: previewData.message }}
                        />
                        <div className="mt-3 inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                          <span className="text-xl">+{previewData.points}</span>
                          <span>puntos</span>
                        </div>
                      </div>
                    </div>

                    <div className="relative z-10 mt-3">
                      <span className="text-xs text-white/90 font-medium bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
                        Hace un momento
                      </span>
                    </div>
                  </div>

                  {/* Actions Footer Preview */}
                  <div className="px-6 py-4 border-t border-gray-50 flex items-center gap-4 text-gray-400 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-full bg-gray-50">♥</div>
                      <span>0</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-full bg-gray-50">💬</div>
                      <span>Comentar</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Info */}
              <div className="p-4 bg-blue-50 border-t border-blue-100">
                <p className="text-xs text-gray-600">
                  <strong>Nota:</strong> Esta es una vista previa de cómo se verá la celebración en el feed. El avatar y nombre corresponden a un usuario de ejemplo.
                </p>
              </div>

              <div className="p-4 border-t border-gray-200 flex justify-end">
                <Button
                  onClick={() => setShowPreview(false)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Cerrar Vista Previa
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog />
    </div>
  );
}

CelebrationEventForm.layout = page => <AppLayout children={page} />;
